import React from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { PageHero, SubNav, SectionHeading, ImageCard, Reveal, CTA, Icon, Eyebrow } from "../components/Shared";
import { IMAGES, AUDIENCES, DIFFERENTIATORS, NAV, SERVICES } from "../mock";

const useSubNav = () => {
  const { pathname } = useLocation();
  return NAV[2].children.map((c) => ({ ...c, active: pathname === c.path }));
};

export const WhoWeServeOverview = () => (
  <>
    <PageHero
      eyebrow="Who we serve"
      title="Built for nonprofits. Every organization is different; your partner should know how."
      intro="Great North Advantage is a one-stop portal for handling all of a nonprofit's administrative and management needs. We work hard every day to ensure that nonprofit staff and Boards of Directors remain focused on making a difference in our communities."
      image={IMAGES.volunteers}
    />
    <SubNav items={useSubNav()} />
    <section className="section" data-testid="who-we-serve-content">
      <div className="container-x grid gap-10 md:grid-cols-2">
        {AUDIENCES.map((a, i) => (
          <ImageCard key={a.slug} index={i + 1} title={a.title} text={a.short} image={a.image} to={`/who-we-serve/${a.slug}`} delay={i * 100} />
        ))}
      </div>
    </section>
    <CTA />
  </>
);

export const AudienceDetail = () => {
  const { slug } = useParams();
  const subNav = useSubNav();
  const a = AUDIENCES.find((x) => x.slug === slug);
  if (!a) return <Navigate to="/who-we-serve" replace />;
  return (
    <>
      <PageHero compact eyebrow="Who we serve" title={a.title} image={a.image} />
      <SubNav items={subNav} />
      <section className="section" data-testid="audience-detail">
        <div className="container-x grid gap-12 lg:grid-cols-12">
          <Reveal className="prose-gn lg:col-span-7">
            {a.body.map((p, i) => (
              <p key={p.slice(0, 30)} className={i === 0 ? "lead" : ""}>{p}</p>
            ))}
            {a.closing && <p className="font-serif text-2xl text-brand-800">{a.closing}</p>}
          </Reveal>
          <div className="lg:col-span-5">
            {a.offers ? (
              <Reveal delay={100} className="rounded-3xl bg-brand-700 p-8 text-white" data-testid="offer-card">
                <Eyebrow light>Member benefit</Eyebrow>
                <h3 className="mt-3 font-serif text-2xl">{a.offerTitle}</h3>
                <p className="mt-2 text-sm text-sand-200">{a.offerIntro}</p>
                <ol className="mt-6 space-y-5">
                  {a.offers.map((o, i) => (
                    <li key={o.title} className="flex gap-4">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/10 font-mono text-xs">{i + 1}</span>
                      <div>
                        <p className="font-semibold">{o.title}</p>
                        <p className="mt-1 text-sm leading-relaxed text-sand-200">{o.text}</p>
                      </div>
                    </li>
                  ))}
                </ol>
                <Button asChild className="btn-pill mt-8 bg-gold-400 text-ink-900 hover:bg-gold-300">
                  <Link to="/nonprofit-review">Request your review <ArrowRight size={15} className="ml-1.5" /></Link>
                </Button>
              </Reveal>
            ) : (
              <Reveal delay={100} className="rounded-3xl border border-brand-100 bg-white p-8">
                <Eyebrow>Services for mission-driven communities</Eyebrow>
                <ul className="mt-5 space-y-3">
                  {SERVICES.map((s) => (
                    <li key={s.slug}>
                      <Link to={`/what-we-do/${s.slug}`} className="flex items-center gap-3 text-[15px] text-ink-700 transition-colors hover:text-brand-700">
                        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand-50 text-brand-700"><Check size={12} /></span>
                        {s.title}
                      </Link>
                    </li>
                  ))}
                </ul>
                <a href="https://greatnorth.net" target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700 hover:text-brand-900">
                  Visit Great North Property Management <ArrowRight size={15} />
                </a>
              </Reveal>
            )}
          </div>
        </div>
      </section>
      <CTA />
    </>
  );
};

export const WhyWeStandOut = () => (
  <>
    <PageHero
      eyebrow="Why we stand out"
      title="Unsurpassed loyalty from the nonprofits we serve."
      intro="At Great North, clients benefit from remarkable service, extensive knowledge of the marketplace and a team of tirelessly dedicated administration and management professionals."
      image={IMAGES.theatre}
    />
    <section className="section" data-testid="why-we-stand-out-content">
      <div className="container-x grid gap-12 lg:grid-cols-12">
        <Reveal className="prose-gn lg:col-span-6">
          <p className="lead">Great North has developed a formula that works for nonprofit organizations and associations. As a result, Great North benefits from unsurpassed client loyalty in an emerging business.</p>
          <p>Great North Advantage has over 36 years of experience handling the administrative and management needs of organizations like yours. We serve as an extension of your organization's team of staff and volunteers and successfully tackle the challenges that distract you, your staff, and your Board of Directors from the work of achieving your organization's mission. Our hard work on your behalf ensures that you can remain focused on making a difference in our communities.</p>
        </Reveal>
        <div className="grid gap-5 sm:grid-cols-2 lg:col-span-6">
          {DIFFERENTIATORS.map((d, i) => (
            <Reveal key={d.title} delay={i * 80} className="card-lift rounded-3xl border border-brand-100 bg-white p-7">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-50 text-brand-700">
                <Icon name={d.icon} size={20} />
              </span>
              <h3 className="mt-5 font-serif text-xl text-ink-900">{d.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-ink-600">{d.text}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
    <section className="section bg-white">
      <div className="container-x">
        <SectionHeading eyebrow="The Great North difference" title="Built on a simple promise since 1990." intro="Exceptional service with local accountability. Your nonprofit board volunteers its time; our job is to make that job easier and your organization stronger." />
        <div className="mt-12 grid gap-px overflow-hidden rounded-3xl border border-brand-100 bg-brand-100 md:grid-cols-3">
          {[
            { n: "01", t: "Local, hands-on service", d: "Nine staffed offices across New England. Your office is nearby, ensuring quick response times and a regular on-site presence." },
            { n: "02", t: "Proven cost savings", d: "Insurance, audit, energy, cleaning and landscape programs that have returned significant savings to nonprofit budgets, dollars redirected to programs." },
            { n: "03", t: "Long-term partnerships", d: "Many of our team have been with Great North for 10 years or more, and our clients stay with us for decades." },
          ].map((x, i) => (
            <Reveal key={x.n} delay={i * 80} className="bg-white p-8 transition-colors duration-300 hover:bg-sand-50">
              <span className="font-mono text-xs tracking-widest text-brand-600">{x.n}</span>
              <h3 className="mt-4 font-serif text-xl text-ink-900">{x.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-ink-600">{x.d}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
    <CTA />
  </>
);
